import type { IEvaultApi } from './use-evault-api';
import type {
	ITransferCommitted,
	ITransferInitiated,
	IVaultDocument,
} from './types';

import { useCallback, useMemo, useRef, useState } from 'react';

import { eligibleTransferTargets, useYourEmail } from './derive';
import { EVAULT_API } from './use-evault-api';

/** Why the recipient list is empty — see `noTargetsReason`. */
export type INoTargetsReason = 'only-you' | 'no-signers' | 'no-common';

/** Choose a recipient → prove it's you → confirm the server's summary → read what happened. */
export type ITransferStep = 'choose' | 'verify' | 'confirm' | 'done';

export interface IUseTransferArgs {
	api: IEvaultApi;
	/** The selection, resolved by the caller so this never refetches. */
	documents: IVaultDocument[];
	/** 403 `EVAULT_NOT_ENTITLED` — a paywall is not a form error (R10). */
	onUnentitled: () => void;
	/** The list is out of date and the modal cannot fix it (409). */
	onRefresh: () => void;
	/** The server-owned per-document price (`GET /evault-addons` → `status.transferPrice`) — never a literal here. */
	transferPrice: number;
}

/**
 * The two-step transfer the API was built for (R6, §2.9).
 *
 * The component upstream called `initiateTransfer` and `commitTransfer`
 * back-to-back inside one `handleSubmit`, which threw away the whole reason the
 * contract has two calls: the server resolves the recipient, checks that they
 * signed every selected document, and returns its OWN summary — and that summary
 * was never shown. An irreversible move of an authoritative original was
 * confirmed against a count the client had computed for itself. Here initiate
 * ends step 1 and the summary IS step 2.
 *
 * CANCELLING IS A REAL CALL. Backing out of step 2 (or closing the modal there,
 * by ×, backdrop or Escape) sends `{action:'cancel'}` so the pending record is
 * resolved immediately instead of sitting until its 15-minute TTL. A pending
 * transfer left behind is not harmless: the next attempt on the same documents
 * is a second pending record, and the audit trail grows an initiated event with
 * no ending.
 */
export const useTransfer = ({
	api,
	documents,
	onUnentitled,
	onRefresh,
	transferPrice,
}: IUseTransferArgs) => {
	const yourEmail = useYourEmail();

	/*
	 * The intersection, not the union: someone who signed two of three selected
	 * documents cannot receive all three, and the server would refuse the whole
	 * transfer. Computing it here means the picker never offers a rejected
	 * choice — and YOURSELF is the other choice that would be rejected.
	 *
	 * The ported helper returns every common signer, which on this stack's own
	 * agreements includes the person reading the screen: they signed it, and
	 * they own the original. `POST /vault-transfers` answers that selection with
	 * 400 "You already own these documents". Filtering it out here rather than
	 * inside `eligibleTransferTargets` keeps that helper the 1:1 port it is
	 * documented to be — this is a property of the SESSION, not of the
	 * documents, and the helper has no session to consult.
	 */
	const commonSigners = useMemo(
		() => eligibleTransferTargets(documents),
		[documents]
	);
	const targets = useMemo(
		() =>
			commonSigners.filter(target => !yourEmail || target.email !== yourEmail),
		[commonSigners, yourEmail]
	);

	/*
	 * WHY THE LIST IS EMPTY — three different situations that used to share one
	 * sentence about "all of the selected documents", which is wrong for a single
	 * document and reads like a bug:
	 *  - `only-you`   everyone eligible is the reader: they are the only signer
	 *                 (a self-signed agreement), so there is nobody else to give
	 *                 the original to.
	 *  - `no-signers` the documents carry no signers at all.
	 *  - `no-common`  several documents are selected and no one signed every one.
	 */
	const noTargetsReason = useMemo((): INoTargetsReason | null => {
		if (targets.length) return null;
		if (commonSigners.length) return 'only-you';
		if (documents.every(doc => !doc.signers?.length)) return 'no-signers';
		return documents.length > 1 ? 'no-common' : 'no-signers';
	}, [commonSigners, documents, targets]);

	const [step, setStep] = useState<ITransferStep>('choose');
	/* Pre-selected when there is exactly one eligible signer — the ordinary
	   two-party agreement, where making someone pick the only option is noise. */
	const [email, setEmail] = useState(() =>
		targets.length === 1 ? targets[0]?.email ?? '' : ''
	);
	/*
	 * THE OWNING ENTITY — the legal owner, beside the person.
	 *
	 * Kept as two pieces of state rather than one object because the checkbox and
	 * the text field are edited independently: ticking "a person, not an
	 * organisation" changes the KIND and only offers to fill the NAME. It never
	 * overwrites a name already typed, and unticking leaves the text exactly as it
	 * is — someone who ticked the box by accident should not lose what they wrote.
	 */
	const [entityName, setEntityName] = useState('');
	const [entityKind, setEntityKind] = useState<'organization' | 'individual'>(
		'organization'
	);
	const [keepRetentionCopy, setKeepRetentionCopy] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	/**
	 * A 409 is terminal for this modal: the pending transfer is already
	 * committed, cancelled or expired, so neither Back nor Transfer can do
	 * anything with it. The buttons collapse to Close and the list refetches.
	 */
	const [conflicted, setConflicted] = useState(false);
	const [initiated, setInitiated] = useState<ITransferInitiated | null>(null);
	const [committed, setCommitted] = useState<ITransferCommitted | null>(null);
	/** Minted by `useFaceVerification` on the `verify` step; `commit` sends it back. */
	const [faceToken, setFaceToken] = useState('');
	/** Rounds of `commit` refused 403 `FACE_VERIFICATION_REQUIRED`. The first sends the
	    reader back to re-capture; from the second on, re-capturing isn't fixing it, so
	    the modal stops bouncing them back to `verify` and says so on `confirm` instead.
	    Reset whenever a transfer is freshly initiated. */
	const faceRefusals = useRef(0);

	/** The server's own count × the server's own price — never computed from a literal. */
	const fee = useMemo(() => {
		const quantity = initiated?.summary?.documentCount ?? 0;
		return { quantity, unit: transferPrice, total: quantity * transferPrice };
	}, [initiated, transferPrice]);

	/**
	 * The "person, not an organisation" checkbox.
	 *
	 * Ticking it offers the selected signer's own name, because that IS the entity
	 * for a natural person and re-typing it is busywork. It is an offer, not a
	 * binding: the field stays editable (a trading name, a maiden name), and a name
	 * already typed is never replaced. Unticking changes only the kind — the text
	 * is the reader's, not the checkbox's.
	 */
	const setIndividual = useCallback(
		(individual: boolean) => {
			setEntityKind(individual ? 'individual' : 'organization');
			if (!individual) return;
			setEntityName(current => {
				if (current.trim()) return current;
				const signer = targets.find(target => target.email === email);
				return signer?.name ?? '';
			});
		},
		[email, targets]
	);

	/** A target AND a legal owner. The server refuses either one missing; this is the
	    same rule, applied before the round trip rather than after it. */
	const canInitiate = Boolean(email) && Boolean(entityName.trim());

	/** `PATCH {action:'cancel'}`, best-effort: it resolves a record, not the UI. */
	const cancelPending = useCallback(async () => {
		if (!initiated || committed) return;
		await api.patch(`${EVAULT_API.VAULT_TRANSFERS}/${initiated._id}`, {
			action: 'cancel',
		});
		setInitiated(null);
	}, [api, initiated, committed]);

	const initiate = useCallback(async () => {
		if (!canInitiate || busy) return;
		setBusy(true);
		setError('');

		const result = await api.post<ITransferInitiated>(
			EVAULT_API.VAULT_TRANSFERS,
			{
				documentIds: documents.map(doc => doc._id),
				newOwnerEmail: email,
				/* Trimmed here too, so what is sent is what the field shows rather than
				   whatever whitespace it collected. The server trims again — it has to,
				   since it cannot trust a client — and answers with its own copy. */
				newOwnerEntity: { name: entityName.trim(), kind: entityKind },
				keepRetentionCopy,
			}
		);
		setBusy(false);

		if (result.kind === 'unentitled') {
			onUnentitled();
			return;
		}
		if (result.kind !== 'ok') {
			/* Verbatim. The server's refusals here are specific by design — "is
			   not a signer on 2 of the selected documents" tells someone exactly
			   which selection to narrow, and a generic fallback would replace an
			   instruction with a shrug. */
			setError(result.message);
			return;
		}

		faceRefusals.current = 0;
		setFaceToken('');
		setInitiated(result.data);
		setStep('verify');
	}, [
		api,
		busy,
		canInitiate,
		documents,
		email,
		entityKind,
		entityName,
		keepRetentionCopy,
		onUnentitled,
	]);

	const back = useCallback(async () => {
		if (busy) return;
		setBusy(true);
		await cancelPending();
		setBusy(false);
		setError('');
		setStep('choose');
	}, [busy, cancelPending]);

	/** The `verify` step's capture succeeded — move to the fee confirmation. */
	const verified = useCallback((token: string) => {
		setFaceToken(token);
		setError('');
		setStep('confirm');
	}, []);

	const commit = useCallback(async () => {
		if (!initiated || busy) return;
		setBusy(true);
		setError('');

		const result = await api.patch<ITransferCommitted>(
			`${EVAULT_API.VAULT_TRANSFERS}/${initiated._id}`,
			{ action: 'commit', faceVerification: faceToken }
		);
		setBusy(false);

		if (result.kind === 'unentitled') {
			onUnentitled();
			return;
		}
		if (result.kind === 'conflict') {
			setError(result.message);
			setConflicted(true);
			/* Whatever the server refused on — already committed, expired, or an
			   original that changed hands in another tab — the list on screen is
			   describing a state that no longer holds. */
			onRefresh();
			return;
		}
		/* The face token was missing, expired or already spent. The first refusal
		   sends the reader back to capture a fresh one — the server's own sentence
		   (its FACE_VERIFICATION_REQUIRED: prefix stripped) travels with them.
		   From the second refusal in the same transfer, going back to re-capture
		   isn't fixing it, so the modal stops bouncing and says so on confirm
		   instead. */
		if (
			result.kind === 'error' &&
			result.status === 403 &&
			result.message.startsWith('FACE_VERIFICATION_REQUIRED')
		) {
			const reason = result.message.replace(
				/^FACE_VERIFICATION_REQUIRED:\s*/,
				''
			);
			faceRefusals.current += 1;
			setFaceToken('');
			if (faceRefusals.current <= 1) {
				setError(reason);
				setStep('verify');
			} else {
				setError("Verification isn't being accepted. Close and try again.");
			}
			return;
		}
		if (result.kind !== 'ok') {
			setError(result.message);
			return;
		}

		setCommitted(result.data);
		setStep('done');
	}, [api, busy, faceToken, initiated, onRefresh, onUnentitled]);

	return {
		targets,
		noTargetsReason,
		step,
		email,
		setEmail,
		entityName,
		setEntityName,
		entityKind,
		setIndividual,
		canInitiate,
		keepRetentionCopy,
		setKeepRetentionCopy,
		busy,
		error,
		setError,
		conflicted,
		initiated,
		committed,
		fee,
		initiate,
		verified,
		back,
		commit,
		cancelPending,
	};
};
